import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

// H1 — shared info card (same API as before). Built on Popover so it is reliable for both mouse and trackpad:
// mouse hover opens · click pins (or opens) · click again closes · click outside / Esc closes.
const Ctx = React.createContext(null)

function HoverCard({ children, openDelay = 100, closeDelay = 120, open: ctrlOpen, onOpenChange }) {
  const [inner, setInner] = React.useState(false)
  const open = ctrlOpen ?? inner
  const [pinned, setPinned] = React.useState(false)
  const timer = React.useRef(null)
  const suppressHover = React.useRef(false)
  const set = React.useCallback((o) => { if (ctrlOpen === undefined) setInner(o); onOpenChange?.(o); if (!o) setPinned(false) }, [ctrlOpen, onOpenChange])
  const clear = () => { clearTimeout(timer.current) }
  const api = {
    open, pinned,
    hoverIn: () => { clear(); if (suppressHover.current || open) return; timer.current = setTimeout(() => set(true), openDelay) },
    hoverOut: () => { clear(); if (pinned) return; timer.current = setTimeout(() => set(false), closeDelay) },
    leaveTrigger: () => { suppressHover.current = false },
    click: () => {
      clear()
      if (!open) { set(true); setPinned(true) }
      else if (!pinned) setPinned(true)
      else { suppressHover.current = true; set(false) }
    },
    close: () => { clear(); set(false) },
  }
  React.useEffect(() => () => clear(), [])
  return (
    <Ctx.Provider value={api}>
      <PopoverPrimitive.Root open={open} onOpenChange={(o) => { if (!o) api.close() }}>{children}</PopoverPrimitive.Root>
    </Ctx.Provider>
  )
}

const isMouse = (e) => e.pointerType === "mouse"

const HoverCardTrigger = React.forwardRef(({ onClick, onPointerEnter, onPointerLeave, ...props }, ref) => {
  const c = React.useContext(Ctx)
  return (
    <PopoverPrimitive.Trigger
      ref={ref}
      data-pinned={c.pinned ? "true" : "false"}
      onPointerEnter={(e) => { onPointerEnter?.(e); if (isMouse(e)) c.hoverIn() }}
      onPointerLeave={(e) => { onPointerLeave?.(e); c.leaveTrigger(); if (isMouse(e)) c.hoverOut() }}
      onClick={(e) => { onClick?.(e); e.preventDefault(); e.stopPropagation(); c.click() }}
      {...props} />
  )
})
HoverCardTrigger.displayName = "HoverCardTrigger"

// always portaled to <body>, always on top (z 9999 inline so callers can't lower it), collision-aware, max 320px, scrolls inside
const HoverCardContent = React.forwardRef(({ className, align = "center", sideOffset = 6, style, onPointerEnter, onPointerLeave, onClick, ...props }, ref) => {
  const c = React.useContext(Ctx)
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        avoidCollisions
        collisionPadding={12}
        sticky="always"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={(e) => { onPointerEnter?.(e); if (isMouse(e)) c.hoverIn() }}
        onPointerLeave={(e) => { onPointerLeave?.(e); if (isMouse(e)) c.hoverOut() }}
        onClick={(e) => { onClick?.(e); e.stopPropagation() }}
        className={cn(
          "w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        style={{
          zIndex: 9999,
          maxWidth: "min(320px, calc(100vw - 24px))",
          maxHeight: "min(60vh, var(--radix-popover-content-available-height, 60vh))",
          overflowY: "auto",
          overflowWrap: "anywhere",
          ...style,
        }}
        {...props} />
    </PopoverPrimitive.Portal>
  )
})
HoverCardContent.displayName = "HoverCardContent"

export { HoverCard, HoverCardTrigger, HoverCardContent }
